import { useLocation, useNavigate } from "react-router-dom";
// material-ui
import { useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  ClickAwayListener,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  Card,
  Fab,
} from "@mui/material";


// project imports
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";
import useAuth from "hooks/useAuth";

// assets
import { IconLogout, IconSettings } from "@tabler/icons";
import useConfig from "hooks/useConfig";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";
import AnimateButton from "ui-component/extended/AnimateButton";
import { userInitials } from "helpers";
import DisplaySettingsModal from "views/modals/display-settings-modal";
import UserSettingsModal from "views/modals/user-settings-modal";

const ProfileSection = () => {
  const theme = useTheme();
  const { borderRadius } = useConfig();
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const { logout, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [openDisplaySettings, setOpenDisplaySettings] = useState(false);
  const [openUserSettings, setOpenUserSettings] = useState(false);
  const anchorRef = useRef(null);

  const handleLogout = () => {
    try {
      logout();
    } catch (err) {
      alert(err);
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };
  
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleCloseDisplaySettings = () => {
    setOpenDisplaySettings(false);
  };
  const handleCloseUserSettings = () => {
    setOpenUserSettings(false);
  };

  return (
    <>
      <Tooltip title="User Settings">
        <Fab
          component="div"
          onClick={handleToggle}
          size="small"
          variant="circular"
          ref={anchorRef}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          sx={{
            borderRadius: 0,
            borderTopLeftRadius: "50%",
            borderBottomLeftRadius: "50%",
            borderTopRightRadius: "50%",
            borderBottomRightRadius: "50%",
            zIndex: theme.zIndex.speedDial,
          }}
        >
          <AnimateButton type="scale">
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                cursor: "pointer",
              }}
            >
              {userInitials(user)}
            </Avatar>
          </AnimateButton>
        </Fab>
      </Tooltip>

      <Popper
        placement="bottom"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, 8],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions in={open} {...TransitionProps}>
              <Paper sx={{ border: "1px solid grey" }}>
                {open && user && (
                  <MainCard
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    <Box sx={{ py: 2, px: 4.5 }}>
                      <Stack direction="row" alignItems="center">
                        <Typography variant="h4">Welcome,&nbsp;</Typography>
                        <Typography
                          component="span"
                          variant="h4"
                          sx={{ fontWeight: 400 }}
                        >
                          {`${user.firstName} ${user.lastName}`}
                        </Typography>
                      </Stack>
                    </Box>

                    <Divider />

                    <Box sx={{ p: 2, pt: 0 }}>
                      <List
                        component="nav"
                        sx={{
                          width: "100%",
                          minWidth: 180,
                          backgroundColor: "#0e0e0e",
                          borderRadius: "10px",
                          [theme.breakpoints.down("md")]: {
                            minWidth: "100%",
                          },
                          "& .MuiListItemButton-root": {
                            mt: 0.5,
                          },
                        }}
                      >
                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          selected={selectedIndex === 0}
                          onClick={() => setOpenUserSettings(true)}
                        >
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                User Settings
                              </Typography>
                            }
                          />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          selected={selectedIndex === 0}
                          onClick={() => setOpenDisplaySettings(true)}
                        >
                          <ListItemIcon>
                            <IconSettings stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                Display Preferences
                              </Typography>
                            }
                          />
                        </ListItemButton>

                        <ListItemButton
                          sx={{ borderRadius: `${borderRadius}px` }}
                          selected={selectedIndex === 4}
                          onClick={handleLogout}
                        >
                          <ListItemIcon>
                            <IconLogout stroke={1.5} size="20px" />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                Logout
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </List>
                    </Box>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>

      <DisplaySettingsModal
        open={openDisplaySettings}
        handleClose={handleCloseDisplaySettings}
      />
      {user && (
        <UserSettingsModal
          data={user}
          open={openUserSettings}
          handleClose={handleCloseUserSettings}
        />
      )}
    </>
  );
};

export default ProfileSection;
